import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Grid,
  VStack,
  HStack,
  Text,
  Button,
  Select,
  Input,
  RangeSlider,
  RangeSliderTrack,
  RangeSliderFilledTrack,
  RangeSliderThumb,
  Checkbox,
  CheckboxGroup,
  Stack,
  Badge,
  IconButton,
  useBreakpointValue,
  Spinner,
  Alert,
  AlertIcon,
  Center,
} from '@chakra-ui/react';
import { FaTh, FaList, FaSearch } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { usePosts } from '../hooks/useApi';
import { Post, categoriesApi, Category } from '../services/api';

const BrowsePage: React.FC = () => {
  const navigate = useNavigate();
  
  // Categories state
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  
  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [priceRange, setPriceRange] = useState([0, 1000]);
  const [selectedConditions, setSelectedConditions] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<'date' | 'price' | 'views'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await categoriesApi.getCategories();
        setCategories(data.categories);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      } finally {
        setCategoriesLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // Build filters object
  const filters = useMemo(() => ({
    search: searchQuery || undefined,
    category: selectedCategory || undefined,
    minPrice: priceRange[0] > 0 ? priceRange[0] : undefined,
    maxPrice: priceRange[1] < 1000 ? priceRange[1] : undefined,
    condition: selectedConditions.length > 0 ? selectedConditions.join(',') : undefined,
    sortBy,
    sortOrder,
    limit: 20,
  }), [searchQuery, selectedCategory, priceRange, selectedConditions, sortBy, sortOrder]);

  // Fetch posts using real API
  const { data: postsData, isLoading, error, refetch } = usePosts(filters);

  const posts = postsData?.items || [];
  const totalCount = postsData?.totalCount || 0;

  // Responsive settings
  const gridColumns = useBreakpointValue({ base: 1, md: 2, lg: 3, xl: 4 });
  const showFilters = useBreakpointValue({ base: false, md: true });

  const handlePostClick = (postId: string) => {
    navigate(`/post/${postId}`);
  };

  const handleSearch = () => {
    refetch();
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('');
    setPriceRange([0, 1000]);
    setSelectedConditions([]);
    setSortBy('date');
    setSortOrder('desc');
  };

  // Helper function to get category name from ID
  const getCategoryName = (categoryId: string) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : categoryId;
  };

  // Loading state
  if (isLoading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Center py={20}>
          <VStack spacing={4}>
            <Spinner size="xl" color="brand.green.medium" />
            <Text>Loading listings...</Text>
          </VStack>
        </Center>
      </Container>
    );
  }

  // Error state
  if (error) {
    return (
      <Container maxW="container.xl" py={8}>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <VStack align="start" spacing={2}>
            <Text fontWeight="bold">Failed to load listings</Text>
            <Text fontSize="sm">
              {error instanceof Error ? error.message : 'An unexpected error occurred'}
            </Text>
            <Button size="sm" onClick={() => refetch()}>
              Try Again
            </Button>
          </VStack>
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Box>
          <Heading size="lg" mb={2}>Browse Tennis Equipment</Heading>
          <Text color="gray.600">
            {totalCount > 0 ? `${totalCount} items found` : 'No tennis equipment found'}
          </Text>
        </Box>

        {/* Search and Filters */}
        <Box bg="white" p={6} borderRadius="lg" shadow="sm" border="1px" borderColor="gray.200">
          <VStack spacing={4}>
            {/* Search Bar */}
            <HStack w="full">
              <Input
                placeholder="Search for tennis equipment..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              <IconButton
                icon={<FaSearch />}
                aria-label="Search"
                colorScheme="brand.green"
                onClick={handleSearch}
              />
            </HStack>

            {/* Filters Row */}
            {showFilters && (
              <Grid templateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={4} w="full">
                {/* Category Filter */}
                <Select
                  placeholder={categoriesLoading ? "Loading categories..." : "All Categories"}
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  disabled={categoriesLoading}
                >
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </Select>

                {/* Sort Options */}
                <HStack>
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as 'date' | 'price' | 'views')}
                  >
                    <option value="date">Date</option>
                    <option value="price">Price</option>
                    <option value="views">Views</option>
                  </Select>
                  <Select
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                  >
                    <option value="desc">High to Low</option>
                    <option value="asc">Low to High</option>
                  </Select>
                </HStack>
              </Grid>
            )}

            {/* Advanced Filters */}
            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={6} w="full">
              {/* Price Range */}
              <Box>
                <Text fontWeight="medium" mb={2}>
                  Price Range: ${priceRange[0]} - ${priceRange[1]}
                </Text>
                <RangeSlider
                  value={priceRange}
                  onChange={setPriceRange}
                  min={0}
                  max={1000}
                  step={10}
                >
                  <RangeSliderTrack>
                    <RangeSliderFilledTrack bg="brand.green.medium" />
                  </RangeSliderTrack>
                  <RangeSliderThumb index={0} />
                  <RangeSliderThumb index={1} />
                </RangeSlider>
              </Box>

              {/* Condition Filter */}
              <Box>
                <Text fontWeight="medium" mb={2}>Condition</Text>
                <CheckboxGroup
                  value={selectedConditions}
                  onChange={(values) => setSelectedConditions(values as string[])}
                >
                  <Stack direction="row" wrap="wrap">
                    <Checkbox value="new">New</Checkbox>
                    <Checkbox value="like-new">Like New</Checkbox>
                    <Checkbox value="good">Good</Checkbox>
                    <Checkbox value="fair">Fair</Checkbox>
                  </Stack>
                </CheckboxGroup>
              </Box>
            </Grid>

            {/* Filter Actions */}
            <HStack justify="space-between" w="full">
              <Button variant="outline" onClick={clearFilters}>
                Clear Filters
              </Button>
              <Button colorScheme="brand.green" onClick={handleSearch}>
                Apply Filters
              </Button>
            </HStack>
          </VStack>
        </Box>

        {/* View Controls */}
        <HStack justify="space-between">
          <HStack>
            <Text fontSize="sm" color="gray.600">
              Showing {posts.length} of {totalCount} items
            </Text>
          </HStack>
          <HStack>
            <IconButton
              icon={<FaTh />}
              aria-label="Grid view"
              variant={viewMode === 'grid' ? 'solid' : 'outline'}
              colorScheme="brand.green"
              size="sm"
              onClick={() => setViewMode('grid')}
            />
            <IconButton
              icon={<FaList />}
              aria-label="List view"
              variant={viewMode === 'list' ? 'solid' : 'outline'}
              colorScheme="brand.green"
              size="sm"
              onClick={() => setViewMode('list')}
            />
          </HStack>
        </HStack>

        {/* Results */}
        {posts.length === 0 ? (
          <Center py={20}>
            <VStack spacing={4}>
              <Text fontSize="lg" color="gray.500">No items found</Text>
              <Text fontSize="sm" color="gray.400">
                Try adjusting your search criteria or filters
              </Text>
              <Button colorScheme="brand.green" onClick={clearFilters}>
                Clear All Filters
              </Button>
            </VStack>
          </Center>
        ) : (
          <Grid
            templateColumns={
              viewMode === 'grid'
                ? `repeat(${gridColumns}, 1fr)`
                : '1fr'
            }
            gap={6}
          >
            {posts.map((post) => (
              <PostCard
                key={post.postId}
                post={post}
                viewMode={viewMode}
                onClick={() => handlePostClick(post.postId)}
                getCategoryName={getCategoryName}
              />
            ))}
          </Grid>
        )}
      </VStack>
    </Container>
  );
};

// Post Card Component
interface PostCardProps {
  post: Post;
  viewMode: 'grid' | 'list';
  onClick: () => void;
  getCategoryName: (categoryId: string) => string;
}

const PostCard: React.FC<PostCardProps> = ({ post, viewMode, onClick, getCategoryName }) => {
  const isListView = viewMode === 'list';

  return (
    <Box
      bg="white"
      borderRadius="lg"
      shadow="sm"
      border="1px"
      borderColor="gray.200"
      overflow="hidden"
      cursor="pointer"
      transition="all 0.2s"
      _hover={{ shadow: 'md', transform: 'translateY(-2px)' }}
      onClick={onClick}
    >
      {isListView ? (
        <HStack spacing={4} p={4}>
          <Box
            w="120px"
            h="120px"
            bg="gray.100"
            borderRadius="md"
            flexShrink={0}
            backgroundImage={post.images?.[0] ? `url(${post.images[0]})` : undefined}
            backgroundSize="cover"
            backgroundPosition="center"
          />
          <VStack align="start" flex={1} spacing={2}>
            <Text fontWeight="bold" fontSize="lg" noOfLines={1}>
              {post.title}
            </Text>
            <Text color="brand.green.medium" fontWeight="bold" fontSize="xl">
              ${post.price}
            </Text>
            <Text color="gray.600" fontSize="sm" noOfLines={2}>
              {post.description}
            </Text>
            <HStack>
              <Badge colorScheme="green" variant="subtle">
                {post.condition}
              </Badge>
              <Badge variant="outline">
                {getCategoryName(post.categoryId)}
              </Badge>
            </HStack>
          </VStack>
        </HStack>
      ) : (
        <VStack spacing={0} align="stretch">
          <Box
            h="200px"
            bg="gray.100"
            backgroundImage={post.images?.[0] ? `url(${post.images[0]})` : undefined}
            backgroundSize="cover"
            backgroundPosition="center"
          />
          <VStack p={4} align="start" spacing={2}>
            <Text fontWeight="bold" fontSize="md" noOfLines={2}>
              {post.title}
            </Text>
            <Text color="brand.green.medium" fontWeight="bold" fontSize="lg">
              ${post.price}
            </Text>
            <HStack>
              <Badge colorScheme="green" variant="subtle" fontSize="xs">
                {post.condition}
              </Badge>
              <Badge variant="outline" fontSize="xs">
                {getCategoryName(post.categoryId)}
              </Badge>
            </HStack>
          </VStack>
        </VStack>
      )}
    </Box>
  );
};

export default BrowsePage; 